import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { LucideAngularModule, Search } from 'lucide-angular';
import { SearchService } from '@app/core/services/search.service';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { OverlayMenuComponent } from '@shared/components/overlay-menu/overlay-menu.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SearchConfig } from '@shared/components/input/search/search-config.interface';
import { SearchDefaultConfig } from '@shared/components/input/search/search-default-config';
import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { SearchResult, SearchResultCollection } from '@shared/components/input/search/search-result.interface';
import { defer, Observable, Subscription } from 'rxjs';

interface SearchLevel {
  value: string;
  results: SearchResultCollection[];
  load?: () => Observable<SearchResultCollection[]>;
}

@Component({
  selector: 'app-input-search',
  imports: [
    LucideAngularModule,
    ReactiveFormsModule,
    OverlayMenuComponent,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css'
})
export class SearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput', { static: false }) public searchInput?: ElementRef;
  @ViewChild('searchOverlayMenu') public searchOverlayMenu!: OverlayMenuComponent;

  /** Config **/
  @Input() public config?: SearchConfig;
  public searchConfig: SearchConfig = SearchDefaultConfig;

  /** Emit Search **/
  @Output() public result: EventEmitter<any> = new EventEmitter();

  /** Emit Hotkey **/
  @Output() public hotkey: EventEmitter<string> = new EventEmitter();

  /** Search **/
  @Output() public searchQuery: EventEmitter<string> = new EventEmitter();

  /** Global Hotkey **/
  @HostListener('document:keydown.meta.k', ['$event'])
  public focusSearch(e: Event) {
    if (e instanceof KeyboardEvent) {
      if (this.searchInput && this.searchConfig.hotkeys) {
        this.searchInput.nativeElement.focus();
      }
    }
  }

  @HostListener('document:keydown.meta.g', ['$event'])
  public toggleAdvancedSearch(e: Event) {
    if (e instanceof KeyboardEvent) {
      this.hotkey.emit(e.key);
    }
  }

  /** Local Hotkey **/
  @HostListener('document:keydown', ['$event'])
  public performSearch(event: Event) {
    if (this.isComponentInFocus && event instanceof KeyboardEvent
      && event.target === this.searchInput?.nativeElement) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          /** If no element is focused, emit the search string **/
          if (this.elementFocus === -1) {
            this.emitSearch(this.searchForm.value);
            return;
          }

          if (this.searchInput && this.config?.referToSearch) {
            this.searchService.addQuery(this.searchInput?.nativeElement.value);

            if (this.searchConfig.navigateUrl) {
              this.navigateToSearch(
                this.searchConfig.navigateUrl,
                this.searchInput.nativeElement.value
              );
            }
          }

          break;
        case 'ArrowDown':
          event.preventDefault();
          break;
        case 'ArrowUp':
          event.preventDefault();
          break;
        case 'Escape':
          this.clearInput();
          break;
      }
    }
  }

  /** Focus Controllers **/
  @HostListener('focusin', ['$event'])
  public onFocusOut(event: FocusEvent) {
    this.isComponentInFocus = true;
    if (this.value && (this.searchResultCollection.length > 0 || this.canGoBack)) {
      this.searchOverlayMenu.visible = true;
    }
  }

  @HostListener('focusout', ['$event'])
  public onBlur(event: FocusEvent) {
    this.isComponentInFocus = false;
  }

  public elementFocus: number = -1;

  /** Component Focused **/
  public isComponentInFocus: boolean = false;

  /** Icons **/
  readonly searchIcon = Search

  private readonly subscriptions = new Subscription();
  private requestSubscription?: Subscription;
  private pendingSearch?: ReturnType<typeof setTimeout>;
  private readonly history: SearchLevel[] = [];
  private currentLoad?: () => Observable<SearchResultCollection[]>;

  public loading = false;
  public loadFailed = false;
  public loadNotFound = false;

  public get canGoBack(): boolean {
    return this.history.length > 0;
  }

  /** Search FormControl **/
  public searchForm: FormControl = new FormControl('');

  /** Autocomplete Suggestions **/
  public autoCompleteSuggestions: any[] = [];

  /** Search Result Collection **/
  public searchResultCollection: SearchResultCollection[] = [];

  /** Value **/
  public value: string = '';

  constructor(
    private readonly searchService: SearchService,
    private readonly router: Router,
  ) {

    this.subscriptions.add(this.searchForm.valueChanges.subscribe((value: string | null) => {
      this.cancelSearch();
      this.value = (value ?? '').trim();
      this.history.length = 0;
      this.currentLoad = undefined;
      this.searchResultCollection = [];
      this.loadFailed = false;
      this.loadNotFound = false;
      this.elementFocus = -1;

      if (!this.value) {
        this.searchOverlayMenu?.close();
        return;
      }

      this.loading = !!this.searchConfig.callBackFunction;
      const query = this.value;
      this.pendingSearch = setTimeout(() => this.handleSearchChange(query), 300);
    }));
  }

  public ngOnInit(): void {
    /** Merge Config **/
    if (this.config) {
      this.searchConfig = {
        ...this.searchConfig,
        ...this.config,
      }
    }
  }

  public ngAfterViewInit(): void {
    if (this.searchConfig) {
      if (this.searchConfig.autoFocus) {
        setTimeout(() => {
          this.searchInput?.nativeElement.focus();
        }, 0);
      }
    }
  }

  public handleSearchChange(value: string): void {
    this.cancelSearch();
    this.history.length = 0;
    this.currentLoad = undefined;
    this.value = value;
    this.searchResultCollection = [];
    this.loadFailed = false;
    this.loadNotFound = false;

    /** Use Callback Service if defined **/
    const callback = this.searchConfig.callBackFunction;
    if (value && callback) {
      this.currentLoad = () => callback(value);
      this.loadResults();
    }
  }

  public navigateToSearch(url: string, id?: number | string): void {
    this.router.navigate([url, String(id)]);
  }

  /** Search Result Clicked - Refer to data collection **/
  public searchResultClicked(item: SearchResult): void {
    if (item.children) {
      this.history.push({
        value: this.value,
        results: this.searchResultCollection,
        load: this.currentLoad,
      });
      this.value = Array.isArray(item.title) ? item.title[0] : item.title ?? this.value;
      this.searchForm.setValue(this.value, { emitEvent: false });
      this.currentLoad = item.children;
      this.loadResults();
      return;
    }

    if (item?.id) {
      /** If there's a navigateUrl defined in the config, take the user there **/
      if (this.searchConfig.navigateUrl) {
        this.navigateToSearch(this.searchConfig.navigateUrl, item.id);
      }

      /** Emit result **/
      this.result.emit(item);

      /** Clear the Input **/
      this.clearInput();
    }
  }

  public emitSearch(value: string): void {
    this.searchQuery.emit(value);

    this.value = '';
    this.searchForm.patchValue(this.value);
  }

  public clearInput(): void {
    this.cancelSearch();
    this.history.length = 0;
    this.currentLoad = undefined;
    this.searchResultCollection = [];
    this.autoCompleteSuggestions = [];
    this.loadFailed = false;
    this.loadNotFound = false;
    this.searchOverlayMenu?.close();
    /** Clear Input **/
    this.value = '';
    this.searchForm.patchValue(this.value, { emitEvent: false });
    /** Defocus Element **/
    this.searchInput?.nativeElement.blur();
  }

  public goBack(): void {
    const previous = this.history.pop();
    if (!previous) {
      return;
    }

    this.cancelSearch();
    this.value = previous.value;
    this.searchForm.setValue(this.value, { emitEvent: false });
    this.searchResultCollection = previous.results;
    this.currentLoad = previous.load;
    this.loadFailed = false;
    this.loadNotFound = false;
    this.elementFocus = -1;
    this.searchOverlayMenu.visible = true;
    this.searchInput?.nativeElement.focus();
  }

  public loadResults(): void {
    if (!this.currentLoad) {
      return;
    }

    this.cancelSearch();
    this.loading = true;
    this.loadFailed = false;
    this.loadNotFound = false;
    this.searchResultCollection = [];
    this.elementFocus = -1;
    if (this.searchOverlayMenu) {
      this.searchOverlayMenu.visible = true;
    }
    this.requestSubscription = defer(this.currentLoad).subscribe({
      next: results => {
        this.searchResultCollection = results.filter(collection => collection.results.length > 0);
        this.loading = false;
      },
      error: (error: unknown) => {
        this.loading = false;
        this.loadFailed = true;
        this.loadNotFound = error instanceof HttpErrorResponse && error.status === 404;
      },
      complete: () => this.loading = false,
    });
  }

  public ngOnDestroy(): void {
    this.cancelSearch();
    this.subscriptions.unsubscribe();
  }

  private cancelSearch(): void {
    clearTimeout(this.pendingSearch);
    this.pendingSearch = undefined;
    this.requestSubscription?.unsubscribe();
    this.loading = false;
  }
}
